// Folds a backend Answer envelope ({type, text, data}) into a single string.
//
// Twin of server/answer_utils.py's fold_answer_text and
// externalChatInterface/answer_render.py's answer_to_text: `text`, a blank
// line, then the body. type="list" renders as a title line plus dash bullets,
// anything else as one line per row - read from `type`, never guessed from the
// shape of `data`.
//
// Needed because /formatans takes a single `chunk` string, while
// FORMAT_DECISION_DIRECTIVE tells the model that a table answer's `text` is
// only a short description and must NOT restate the rows. Passing `text`
// alone leaves "Format as chart" with nothing but "Here is a comparison of
// the three plans." and it invents the numbers.
//
// Cells are joined with " | " and never rewritten (fold_answer_text does the
// same) so a value like "1,234" survives intact.
// The points of a list answer, one per row.
//
// `data` arrives exactly as the model produced it - a one-column grid, because
// the field it comes from is typed List[List[str]] and providers reject union
// arrays. Each row holds one point, so each receiver unpacks it itself rather
// than the server reshaping the payload on the way out.
export const listPoints = (data) =>
    (Array.isArray(data) ? data : []).map((row) =>
        Array.isArray(row) ? (row.length ? String(row[0]) : '') : String(row)
    );

export const answerToText = (answer) => {
    if (answer === null || answer === undefined) {
        return '';
    }
    if (typeof answer === 'string') {
        return answer;
    }
    if (typeof answer !== 'object' || Array.isArray(answer)) {
        return String(answer);
    }

    const hasText = answer.text !== null && answer.text !== undefined;
    const data = Array.isArray(answer.data) ? answer.data : null;

    if (!hasText && !data) {
        // Not envelope-shaped - a schema caller's template object. JSON is a
        // more useful thing to hand a formatter than "[object Object]".
        try {
            return JSON.stringify(answer);
        } catch (e) {
            return '';
        }
    }

    const text = hasText ? String(answer.text) : '';

    if (!data || data.length === 0) {
        return text;
    }

    let body;
    if (answer.type === 'list') {
        const points = listPoints(data);
        const bullets = points
            .slice(1)
            .map((item) => `- ${item}`)
            .join('\n');
        body = bullets ? `${points[0]}\n${bullets}` : points[0];
    } else {
        body = data
            .map((row) =>
                (Array.isArray(row) ? row : [row])
                    .map((cell) => String(cell))
                    .join(' | ')
            )
            .join('\n');
    }

    return text ? `${text}\n\n${body}` : body;
};
